/**
 * Main Table Component
 * Renders a responsive data table with header, body, and export functionality
 *
 * @param {Object[]} merchants - Array of merchant data objects
 * @param {string[]} headers - Array of column header strings
 * @param {boolean} isLoading - Loading state of the table
 * @param {Function} onBusinessClick - Handler for business name click
 */
import React, {
  memo,
  useMemo,
  useEffect,
  useRef,
} from "react";
import TableSkeleton from "../TableSkeleton/TableSkeleton";
import TableBody from "./TableBody";
import SortableHeader from "./SortableHeader";
import ResultsLoadMorePanel from "../ResultsLoadMorePanel/ResultsLoadMorePanel";
import "./Table.scss";

const Table = memo(
  ({
    merchants = [],
    headers = [],
    isLoading = false,
    onBusinessClick,
    onSort = null,
    sortColumn = null,
    sortDirection = null,
    secondarySortColumn = null,
    secondarySortDirection = null,
    rows = 50,
    company = null,
    isLoadingMore = false,
    hasMore = false,
    onLoadMore = null,
    isMerchantListView = false,
    hideFooter = false,
    footerLoadingState = isLoading,
    disableLoadMoreButton = false
  }) => {
    const tableRef = useRef(null);
    const theadRef = useRef(null);
    const tbodyRef = useRef(null);

    // Memoize header rows to prevent unnecessary re-renders
    const headerRows = useMemo(
      () => (
        <tr>
          {headers.map((header, index) => {
            // Check if this column is sortable (Date Submitted or Date Approved)
            const isSortable = header === "Date Submitted" || header === "Date Approved";
            const currentSortDirection = sortColumn === index ? sortDirection : null;
            
            return (
              <SortableHeader
                key={`header-${header}-${index}`}
                header={header}
                isSortable={isSortable}
                sortDirection={currentSortDirection}
                secondarySortDirection={index === secondarySortColumn ? secondarySortDirection : null}
                onSort={onSort}
                columnIndex={index}
              />
            );
          })}
        </tr>
      ),
      [headers, sortColumn, sortDirection, secondarySortColumn, secondarySortDirection, onSort]
    );


    // Remove dynamic height calculation - let table grow naturally
    // The table will now expand based on content without height constraints

    // Sticky header effect for browser scroll
    useEffect(() => {
      const table = tableRef.current;
      const thead = theadRef.current;

      if (!table || !thead) return;

      const handleStickyScroll = () => {
        const tableRect = table.getBoundingClientRect();
        
        // Add scrolling class when table header is at the top of viewport
        if (tableRect.top <= 0) {
          thead.classList.add("scrolling");
        } else {
          thead.classList.remove("scrolling");
        }
      };

      window.addEventListener("scroll", handleStickyScroll);
      return () => window.removeEventListener("scroll", handleStickyScroll);
    }, [merchants.length]);

    // Determine if this is a simple table (2 columns)
    const isSimpleTable = headers.length === 2;

    // Memoize the table to prevent unnecessary re-renders
    return (
      <div className={`table-wrapper ${isSimpleTable ? 'simple-table' : ''} ${isMerchantListView ? 'merchant-list-view' : ''}`}>
        {/* Main table content with sticky header and optimized scrolling */}
        <div className="table-responsive custom-table position-relative business-app-table">
          <table ref={tableRef} className="table table-sticky-header table-hover">
            <thead ref={theadRef} className="sticky-header">
              {headerRows}
            </thead>
            {/* Show skeleton loader only in tbody during loading */}
            {isLoading ? (
              <TableSkeleton
                rows={rows}
                columns={headers.length}
                tbodyOnly={true}
              />
            ) : (
              <TableBody
                merchants={merchants}
                headers={headers}
                onBusinessClick={onBusinessClick}
                tbodyRef={tbodyRef}
                company={company}
                isMerchantListView={isMerchantListView}
              />
            )}

            {/* Infinite scroll skeleton loader - shown as tbody */}
            {isLoadingMore && (
              <TableSkeleton
                columns={headers.length}
                rows={5}
                infiniteScroll={true}
              />
            )}

          </table>
        </div>

        {/* Load More Results Button - show only if there are merchants and not loading */}
        {!hideFooter && (
          <ResultsLoadMorePanel
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={(shouldScroll) => {
              if (onLoadMore) {
                onLoadMore();
                // Auto scroll down to show loading area if shouldScroll is true
                if (shouldScroll) {
                  setTimeout(() => {
                    window.scrollTo({
                      top: document.documentElement.scrollHeight,
                      behavior: 'smooth'
                    });
                  }, 100);
                }
              }
            }}
            loading={footerLoadingState}
            itemsCount={merchants.length}
            shouldScroll={true}
            forceDisableButton={disableLoadMoreButton}
          />
        )}
      </div>
    );
  }
);

// Add display name for debugging
Table.displayName = "Table";

export default Table;
